import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, RefreshCw, Search, Pencil, Trash2,
  ChevronUp, ChevronDown, ImageIcon, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useDataRefresh } from '../../context/DataRefreshContext';
import BannerFormModal from '../../components/admin/BannerFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import '../../components/admin/MobileCardList.css';

interface Banner {
  id: number;
  title_bn: string;
  title_en: string;
  subtitle_bn: string;
  subtitle_en: string;
  image: string;
  link: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

const BannerManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('display_order');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { triggerRefresh } = useDataRefresh();
  const [toast, setToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });


  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getBanners();
      const data = response.data || response;
      const bannersList = Array.isArray(data) ? data : (data.results || data.data || []);
      setBanners(bannersList);
    } catch (error) {
      console.error('Failed to load banners:', error);
      setToast({ show: true, message: 'Failed to load banners', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner: Banner) => {
    setSelectedBanner(banner);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (banner: Banner) => {
    setSelectedBanner(banner);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;

    try {
      await adminAPI.deleteBanner(selectedBanner.id);
      setToast({ show: true, message: 'Banner deleted successfully', type: 'success' });
      loadBanners();
      // ✅ Trigger refresh for banner data
      triggerRefresh('banners');
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete banner', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedBanner(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      // Create FormData object for file upload
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image') {
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else {
          data.append(key, formData[key]);
        }
      });

      if (selectedBanner) {
        await adminAPI.updateBanner(selectedBanner.id, data);
        setToast({ show: true, message: 'Banner updated successfully', type: 'success' });
      } else {
        await adminAPI.createBanner(data);
        setToast({ show: true, message: 'Banner created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedBanner(null);
      loadBanners();
      // ✅ Trigger refresh for banner data
      triggerRefresh('banners');
    } catch (error) {
      console.error('Save error:', error);
      setToast({ show: true, message: 'Failed to save banner', type: 'error' });
    }
  };

  const handleAddNew = () => {
    setSelectedBanner(null);
    setIsModalOpen(true);
  };

  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  const bannersArray = useMemo(() => (Array.isArray(banners) ? banners : []), [banners]);
  const filtered = useMemo(() => {
    let list = bannersArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(b =>
        b.title_en?.toLowerCase().includes(q) ||
        b.title_bn?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [bannersArray, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Banner Management</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAddNew}>
            <Plus size={13} /> Add Banner <span className="adm-btn-badge">{banners.length}</span>
          </button>
          <button className="adm-btn adm-btn-refresh" onClick={loadBanners} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <span className="adm-count"><strong>{filtered.length}</strong> banners</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by title…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading banners…</div>}
          {!loading && filtered.length === 0 && <div className="mc-empty">No banners found.</div>}
          {!loading && filtered.map(b => (
            <div className="mobile-card" key={b.id}>
              <div className="mc-top">
                {b.image ? <img src={b.image} alt={b.title_en} className="mc-thumb" /> : <div className="mc-thumb-placeholder"><ImageIcon size={22} /></div>}
                <div className="mc-info">
                  <div className="mc-title">{b.title_en}</div>
                  <div className="mc-subtitle">{b.title_bn}</div>
                </div>
                <div className="mc-badge-corner">
                  <span className={`mc-active-pill ${b.is_active ? 'active' : 'inactive'}`}>{b.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className="mc-meta">
                <div className="mc-meta-item"><span className="mc-meta-label">Order:</span><span className="mc-meta-value">{b.display_order}</span></div>
              </div>
              <div className="mc-actions">
                <button className="mc-btn mc-btn-edit" onClick={() => handleEdit(b)}><Pencil size={12} /> Edit</button>
                <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(b)}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div className="adm-table-card">
          <div className="adm-table-scroll">
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th" style={{ width: 60 }}>Image</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('title_en')}>Title EN <SA field="title_en" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('title_bn')}>Title BN <SA field="title_bn" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('display_order')}>Order <SA field="display_order" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                  <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading banners…</td></tr>}
                {!loading && paginated.length === 0 && <tr><td colSpan={6} className="adm-empty">No banners found.</td></tr>}
                {!loading && paginated.map(b => (
                  <tr className="adm-row" key={b.id}>
                    <td className="adm-td">
                      {b.image
                        ? <img src={b.image} alt={b.title_en} className="adm-img" />
                        : <div className="adm-img-placeholder"><ImageIcon size={18} /></div>}
                    </td>
                    <td className="adm-td"><span className="adm-name-main">{b.title_en}</span></td>
                    <td className="adm-td"><span className="adm-name-sub" style={{ fontSize: 12, color: '#64748b' }}>{b.title_bn}</span></td>
                    <td className="adm-td"><span className="adm-text-bold">{b.display_order}</span></td>
                    <td className="adm-td">
                      <span className={`adm-chip ${b.is_active ? 'adm-chip-green' : 'adm-chip-red'}`}>
                        {b.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}
                      </span>
                    </td>
                    <td className="adm-td" style={{ padding: '6px 4px' }}>
                      <div className="adm-actions-row">
                        <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(b)} title="Edit"><Pencil size={12} /></button>
                        <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(b)} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-pagination">
            <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
            <div className="adm-page-info">{filtered.length === 0 ? '0 banners' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
            <div className="adm-page-controls">
              <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
              <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
              <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
            </div>
          </div>
        </div>
      )}

      <BannerFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBanner(null);
        }}
        onSave={handleSave}
        banner={selectedBanner}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedBanner(null);
        }}
        onConfirm={handleDelete}
        title="Delete Banner"
        message={`Are you sure you want to delete "${selectedBanner?.title_en}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default BannerManagement;
